import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

// Base context interface that all contexts must implement
export interface Context {
  id: string;
  name: string;
  type: string; // e.g., 'tab', 'list', 'form', 'table', etc.
}

// Generic context provider interface
export interface ContextProvider<T extends Context = Context> {
  getContext(): T;
  isActive(): boolean;
}

// Context action handler type
export type ContextActionHandler<T extends Context = Context> = (context: T) => void;

// Specific context types
export interface TabContext extends Context {
  type: 'tab';
  tabs: TabItem[];
  activeTabId?: string;
  switchTab: (tabId: string) => void;
  getNextTab: () => TabItem | null;
  getPrevTab: () => TabItem | null;
}

export interface TabItem {
  id: string;
  label: string;
  active?: boolean;
  [key: string]: any;
}

// Table context interface
export interface TableContext extends Context {
  type: 'table';
  hasSelection: boolean;
  selectedCount: number;
  totalCount: number;
  canSelectAll: boolean;
  selectAll: () => void;
  deselectAll: () => void;
  clearSelection: () => void;
  isAllSelected: () => boolean;
  hasPartialSelection: () => boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ContextRegistryService implements OnDestroy {
  private contexts = new Map<string, ContextProvider<any>>();
  private activeContext$ = new BehaviorSubject<string | null>(null);
  private destroy$ = new Subject<void>();

  constructor() {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Register a context provider
   */
  registerContext<T extends Context>(id: string, provider: ContextProvider<T>): void {
    this.contexts.set(id, provider);
  }

  /**
   * Unregister a context provider
   */
  unregisterContext(id: string): void {
    this.contexts.delete(id);
    if (this.activeContext$.value === id) {
      this.activeContext$.next(null);
    }
  }

  /**
   * Set the active context
   */
  setActiveContext(id: string): void {
    if (this.contexts.has(id)) {
      this.activeContext$.next(id);
    }
  }

  /**
   * Get the current active context
   */
  getActiveContext(): Observable<string | null> {
    return this.activeContext$.asObservable();
  }

  /**
   * Get the current active context snapshot
   */
  getActiveContextSnapshot(): string | null {
    return this.activeContext$.value;
  }

  /**
   * Get the active context provider
   */
  getActiveContextProvider<T extends Context = Context>(): ContextProvider<T> | null {
    const activeId = this.activeContext$.value;
    if (activeId && this.contexts.has(activeId)) {
      return this.contexts.get(activeId)! as ContextProvider<T>;
    }
    return null;
  }

  /**
   * Get all registered contexts
   */
  getRegisteredContexts(): string[] {
    return Array.from(this.contexts.keys());
  }

  /**
   * Get contexts by type
   */
  getContextsByType<T extends Context>(
    type: string
  ): Array<{ id: string; provider: ContextProvider<T> }> {
    const result: Array<{ id: string; provider: ContextProvider<T> }> = [];

    for (const [id, provider] of this.contexts) {
      const context = provider.getContext();
      if (context.type === type) {
        result.push({ id, provider: provider as ContextProvider<T> });
      }
    }

    return result;
  }

  /**
   * Check if a context is registered
   */
  hasContext(id: string): boolean {
    return this.contexts.has(id);
  }

  /**
   * Auto-detect active context based on provider's isActive() method
   */
  autoDetectActiveContext(): string | null {
    for (const [id, provider] of this.contexts) {
      if (provider.isActive()) {
        this.setActiveContext(id);
        return id;
      }
    }
    return null;
  }

  /**
   * Execute a generic context-aware action
   */
  executeContextAction<T extends Context = Context>(action: ContextActionHandler<T>): boolean {
    const provider = this.getActiveContextProvider();

    if (provider) {
      const context = provider.getContext() as unknown as T;
      action(context);
      return true;
    }

    // Try auto-detecting active context
    const autoDetectedId = this.autoDetectActiveContext();

    if (autoDetectedId) {
      const autoProvider = this.getActiveContextProvider();
      if (autoProvider) {
        const autoContext = autoProvider.getContext() as unknown as T;
        action(autoContext);
        return true;
      }
    }

    return false;
  }

  /**
   * Execute action on contexts of a specific type
   */
  executeContextActionByType<T extends Context>(
    type: string,
    action: ContextActionHandler<T>
  ): boolean {
    // First try the active context if it matches the type
    const activeProvider = this.getActiveContextProvider();
    if (activeProvider) {
      const context = activeProvider.getContext();
      if (context.type === type) {
        action(context as unknown as T);
        return true;
      }
    }

    // If active context doesn't match type, find first matching context
    const matchingContexts = this.getContextsByType<T>(type);
    if (matchingContexts.length > 0) {
      const firstMatch = matchingContexts[0];
      if (firstMatch.provider.isActive()) {
        this.setActiveContext(firstMatch.id);
        action(firstMatch.provider.getContext() as unknown as T);
        return true;
      }
    }

    return false;
  }

  // Backward compatibility methods for existing tab functionality
  /**
   * Switch to next tab in the active context
   */
  switchToNextTab(): boolean {
    return this.executeContextActionByType<TabContext>('tab', context => {
      const nextTab = context.getNextTab();
      if (nextTab) {
        context.switchTab(nextTab.id);
      }
    });
  }

  /**
   * Switch to previous tab in the active context
   */
  switchToPrevTab(): boolean {
    return this.executeContextActionByType<TabContext>('tab', context => {
      const prevTab = context.getPrevTab();
      if (prevTab) {
        context.switchTab(prevTab.id);
      }
    });
  }

  // Table context methods
  /**
   * Select all rows in the active table context
   */
  selectAllTableRows(): boolean {
    return this.executeContextActionByType<TableContext>('table', context => {
      if (context.canSelectAll && !context.isAllSelected()) {
        context.selectAll();
      }
    });
  }

  /**
   * Deselect all rows in the active table context
   */
  deselectAllTableRows(): boolean {
    return this.executeContextActionByType<TableContext>('table', context => {
      if (context.hasSelection) {
        context.deselectAll();
      }
    });
  }
}
